import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { HardHat, FileText, DownloadSimple, CheckCircle, FileArrowUp } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SubInvite() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ sub_name: "", company: "", address: "", phone: "", pricing: "", clarifications: "", exclusions: "", alternate_pricing: "" });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef();

  const load = () => axios.get(`${API}/sub-invite/${token}`)
    .then(({ data }) => {
      setData(data);
      setFiles(data.files || []);
      if (data.bid) {
        setForm({
          sub_name: data.bid.sub_name || "", company: data.bid.company || "", address: data.bid.address || "",
          phone: data.bid.phone || "", pricing: data.bid.pricing || "", clarifications: data.bid.clarifications || "",
          exclusions: data.bid.exclusions || "", alternate_pricing: data.bid.alternate_pricing || "",
        });
        setSubmitted(true);
      }
    })
    .catch(() => setNotFound(true));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFilePick = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setUploading(true);
    try {
      for (const file of picked) {
        const fd = new FormData();
        fd.append("file", file);
        const { data: rec } = await axios.post(`${API}/sub-invite/${token}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => [...prev, rec]);
      }
      toast.success("File(s) uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/sub-invite/${token}/bid`, {
        ...form, pricing: parseFloat(form.pricing) || 0, file_ids: files.map((f) => f.id),
      });
      toast.success("Bid submitted successfully");
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  if (notFound) return <Center>This invitation link is invalid or has expired.</Center>;
  if (!data) return <Center>Loading invitation…</Center>;

  const { project, documents, invitation } = data;

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-[hsl(var(--blueprint))] text-white">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center gap-2">
          <div className="h-8 w-8 bg-primary flex items-center justify-center"><HardHat size={20} weight="fill" /></div>
          <span className="font-heading font-black text-lg tracking-tighter">CONSTRUCT<span className="text-primary">HUB</span></span>
          <span className="ml-auto overline text-white/60">Subcontractor Bid Portal</span>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="overline text-primary mb-2">{invitation.trade} Bid Invitation</div>
        <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">{project?.name}</h1>
        <p className="text-muted-foreground mt-1">{project?.brand} · {project?.address}</p>
        <div className="grid sm:grid-cols-3 gap-4 mt-6">
          <Info label="Trade" value={invitation.trade} />
          <Info label="Bid Due Date" value={invitation.bid_due_date || "TBD"} />
          <Info label="Project Number" value={project?.number || "—"} />
        </div>
        {invitation.scope && <div className="mt-4"><div className="overline text-muted-foreground mb-1">Scope Package</div><p className="text-sm text-muted-foreground">{invitation.scope}</p></div>}
        {invitation.message && <div className="mt-4 border-l-2 border-primary pl-4 text-sm text-muted-foreground">{invitation.message}</div>}

        <div className="grid lg:grid-cols-2 gap-8 mt-8">
          <div>
            <h2 className="font-heading font-bold text-lg tracking-tight mb-3">Project Plans</h2>
            {documents?.length ? (
              <div className="border border-border bg-card divide-y divide-border">
                {documents.map((d) => (
                  <div key={d.id} className="flex items-center gap-3 px-4 py-3" data-testid={`sub-doc-${d.id}`}>
                    <FileText size={22} weight="duotone" className="text-primary shrink-0" />
                    <div className="min-w-0 flex-1"><div className="font-medium truncate">{d.name}</div><div className="text-xs text-muted-foreground">{d.category} · v{d.current_version}</div></div>
                    <a href={`${API}/sub-invite/${token}/document/${d.id}`} target="_blank" rel="noreferrer" data-testid={`sub-doc-download-${d.id}`} className="text-muted-foreground hover:text-primary transition-colors duration-150"><DownloadSimple size={20} /></a>
                  </div>
                ))}
              </div>
            ) : <div className="border border-dashed border-border p-8 text-center text-muted-foreground text-sm">No plans uploaded yet.</div>}
          </div>

          <div>
            <h2 className="font-heading font-bold text-lg tracking-tight mb-3">Submit Your Bid</h2>
            {submitted && (
              <div className="border border-primary bg-primary/5 p-3 mb-4 flex items-center gap-2 text-sm" data-testid="sub-bid-submitted-banner">
                <CheckCircle size={20} weight="fill" className="text-primary" /> Bid submitted. You can update it below.
              </div>
            )}
            <form onSubmit={submit} className="space-y-4 bg-card border border-border p-5" data-testid="sub-bid-form">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="overline">Your Name *</Label><Input value={form.sub_name} onChange={set("sub_name")} required data-testid="sub-bid-name-input" className="rounded-none h-11" /></div>
                <div className="space-y-2"><Label className="overline">Company</Label><Input value={form.company} onChange={set("company")} data-testid="sub-bid-company-input" className="rounded-none h-11" /></div>
              </div>
              <div className="space-y-2"><Label className="overline">Address</Label><Input value={form.address} onChange={set("address")} data-testid="sub-bid-address-input" className="rounded-none h-11" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="overline">Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="sub-bid-phone-input" className="rounded-none h-11" /></div>
                <div className="space-y-2"><Label className="overline">Pricing *</Label><Input type="number" value={form.pricing} onChange={set("pricing")} required data-testid="sub-bid-pricing-input" className="rounded-none h-11" /></div>
              </div>
              <div className="space-y-2"><Label className="overline">Clarifications</Label><Textarea value={form.clarifications} onChange={set("clarifications")} data-testid="sub-bid-clarifications-input" className="rounded-none" rows={2} /></div>
              <div className="space-y-2"><Label className="overline">Exclusions</Label><Textarea value={form.exclusions} onChange={set("exclusions")} data-testid="sub-bid-exclusions-input" className="rounded-none" rows={2} /></div>
              <div className="space-y-2"><Label className="overline">Alternate Pricing</Label><Textarea value={form.alternate_pricing} onChange={set("alternate_pricing")} data-testid="sub-bid-alternate-input" className="rounded-none" rows={2} /></div>

              <div className="space-y-2">
                <Label className="overline">Upload Bid Documents</Label>
                <div className="border border-dashed border-border p-4">
                  <input ref={fileRef} type="file" multiple onChange={onFilePick} data-testid="sub-bid-file-input" className="text-sm w-full file:mr-3 file:border-0 file:bg-secondary file:px-3 file:py-1.5 file:text-sm" />
                  {uploading && <div className="text-xs text-muted-foreground mt-2 animate-pulse">Uploading…</div>}
                  {files.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {files.map((f) => (
                        <div key={f.id} className="flex items-center gap-2 text-sm bg-secondary px-2 py-1.5" data-testid={`sub-uploaded-file-${f.id}`}>
                          <FileArrowUp size={16} className="text-primary" />
                          <span className="truncate flex-1">{f.original_filename}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <Button type="submit" disabled={busy || uploading} data-testid="sub-submit-bid-button" className="w-full rounded-none h-11 bg-primary hover:bg-primary/90 text-white">
                {busy ? "Submitting…" : submitted ? "Update Bid" : "Submit Bid"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

const Center = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-background"><div className="overline text-muted-foreground">{children}</div></div>
);
const Info = ({ label, value }) => (
  <div className="bg-card border border-border p-4"><div className="overline text-muted-foreground mb-1">{label}</div><div className="font-heading font-bold text-lg tracking-tight">{value}</div></div>
);
